import type { BatteryRebateSchedule } from "./types.js";

// Cheaper Home Batteries (SRES) rebate schedule for installs from 1 May 2026 —
// the CURRENT / live schedule (open-ended until the next twice-yearly step).
//
// DATE-KEYED, NOT FY-keyed. The Cheaper Home Batteries program started
// 1 July 2025; from 1 May 2026 the discount became tiered and the STC deeming
// factor stepped DOWN to 6.8 STCs per usable kWh. The factor steps down again
// twice-yearly (Jan + Jul) toward the 2030 SRES wind-down, so:
//
//   RELOAD NOTE — re-verify CER each JANUARY and JULY and, when the factor
//   steps, CLOSE this schedule (`effectiveTo`) and register a NEW dated file.
//   This is a higher reprice cadence than the annual au06/08/13 FY mold.
//
// VERIFICATION (figure ledger
//   docs/research/discovery/verification/home-battery-rebate-sizer-figures.md,
//   checked 2026-06-22):
//   - Fig 2 (CITED to CER primary): from 1 May 2026 the STC deeming factor is
//     6.8 STCs/usable kWh (down from 8.4 Jan–Apr 2026). The dossier's stale
//     ~$271/$162/$40-per-kWh (8.4-STC) values are NOT used — corrected here.
//   - Capacity taper 100% / 60% / 15% across 0–14 / 14–28 / 28–50 usable kWh,
//     nothing above 50 kWh usable; 5 kWh practical usable min, 100 kWh nominal
//     max (CER-primary, CITED).
//   - Fig 4 (CITED): deeming steps twice-yearly (Jan + Jul) from May 2026.
//   - STC price FLOATS — spot has traded ~$39.6–$39.95, near the $40 ex-GST
//     regulated clearing-house cap (ledger Fig 4 / 4b). The per-kWh DOLLAR is a
//     secondary estimate; the STC UNITS + taper + cap are CER-primary. We model
//     a conservative net-of-admin ~$37 indicative price and flag it as such.
export const rates_2026_05_01: BatteryRebateSchedule = {
  product: "SOLAR_BATTERY",
  scheduleKey: "2026-05-01",
  scheduleLabel: "Cheaper Home Batteries schedule from 1 May 2026 (6.8 STC/kWh)",
  effectiveFrom: "2026-05-01",
  effectiveTo: null,
  sources: {
    scheme: "https://www.energy.gov.au/rebates/cheaper-home-batteries-program",
    cerSolarBatteries: "https://cer.gov.au/schemes/small-scale-renewable-energy-scheme/solar-batteries",
    cerChange2026May:
      "https://cer.gov.au/news-and-media/news/2026/february/changes-to-rebate-solar-batteries-1-may",
    dcceewEligibility:
      "https://www.dcceew.gov.au/energy/renewable/cheaper-home-batteries/eligibility-information",
    stcClearingHouse:
      "https://cer.gov.au/schemes/small-scale-renewable-energy-scheme/small-scale-technology-certificates/buy-and-sell-small-scale-technology-certificates",
  },
  sourcesVerifiedAt: "2026-06-22",
  // CURRENT deeming factor from 1 May 2026 — CER-primary, CITED.
  stcDeemingFactor: 6.8,
  taper: [
    { fromKwh: 0, toKwh: 14, factor: 1.0 },
    { fromKwh: 14, toKwh: 28, factor: 0.6 },
    { fromKwh: 28, toKwh: 50, factor: 0.15 },
  ],
  minUsableKwh: 5,
  maxNominalKwh: 100,
  discountedUsableCapKwh: 50,
  // INDICATIVE only — the STC price floats near the $40 ex-GST cap. Conservative
  // net-of-admin estimate; the calc surfaces this as "≈ $X at an STC price of $37".
  indicativeStcPriceAud: 37,
  stcClearingHouseCapAud: 40,
};
